/**
 * AtlasCombatHudModule — the ground-combat HUD (2026-08-13).
 *
 * Layout follows the researched combat-UI doctrine: vitals + the action bar
 * sit BOTTOM-CENTER under the avatar (track health without looking away from
 * the action), the target frame above them, and the screen center stays
 * clear. Elements:
 *
 *   · player health bar (player_vitals — server-authoritative)
 *   · action bar: Attack (F — dispatched as the real hotkey so WildsModule's
 *     combat path is reused untouched) with a COOLDOWN SWEEP from the
 *     equipped weapon's cadence, then one slot per OWNED weapon (crafted
 *     playerItem rows) — click to equip (useItem) / unequip; icons from
 *     textures/icons/<key>.png with tier pips, stats in the tooltip
 *   · target frame: nearest living creature — name, health, distance
 *   · the equipped weapon rides the avatar's hand (prop:weapon_<key> GLB)
 *
 * Everything is a mirror of replicated tables; every action is a reducer.
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { Phase, makeSystem } from '../engine-compat.js';
import { WEAPONS, FISTS, weaponDamage, type WeaponSpec } from '../lib/weapon_catalog.js';
import { prefabAssetId } from '../lib/player_models.js';
import { sfx, sfxPreload } from '../lib/sfx.js';
import { ringVoiceKeys, type VoicedWeapon } from '../lib/audio/weapon_voices.js';
import { nextWeapon, type RingWeapon } from '../lib/weapon_cycle.js';
import { chargeAfter, feedReady, shotsPerCharge, requiredCooldownMs } from '../lib/combat_core.js';
import { hitStop, cameraImpulse, KICK_DEALT_PITCH } from '../lib/combat_feel.js';
import type { AssetModule } from './AssetModule.js';
import { presentIndoors } from '../lib/indoor_body.js'; // (slice H) the gun in hand indoors is lit by the room

const ASSET_STORE_BASE = '/assets-proxy/atlas-assets';

interface VitalsRow { identity: { toHexString(): string }; hp: number; maxHp: number; lastStrikeAt: { microsSinceUnixEpoch: bigint } }
interface EquipRow { equipId: bigint; owner: { isEqual(o: unknown): boolean }; slot: string; itemKey: string; quality: number }
interface ItemRow { itemId: bigint; owner: { isEqual(o: unknown): boolean }; itemKey: string; quality: number }
interface WeaponStateRow { identity: { isEqual(o: unknown): boolean }; itemKey: string; rounds: number; charge: number; chargeAt: { microsSinceUnixEpoch: bigint }; reloadUntil: { microsSinceUnixEpoch: bigint } }
interface CreatureRow { creatureId: bigint; species: string; x: number; z: number; hp: number; state: string }
/** Build-now waves 4–5: my stance (guard / parry / hit-confirm) and my whiff stamp, as the HUD reads them. */
interface StanceRow {
  identity: { toHexString(): string };
  guardRaisedAt: { microsSinceUnixEpoch: bigint }; guardReleasedAt: { microsSinceUnixEpoch: bigint };
  lastParryAt: { microsSinceUnixEpoch: bigint }; lastLandedAt: { microsSinceUnixEpoch: bigint };
}
interface ActionRow { identity: { toHexString(): string }; lastWhiffAt: { microsSinceUnixEpoch: bigint } }

const PLATE: Record<string, string> = {
  ridge_stalker: 'Ridge Stalker', spore_drone: 'Spore Drone', duneback_brute: 'Duneback Brute',
  mire_lurker: 'Mire Lurker', crag_wyrm: 'Crag Wyrm', glass_shrike: 'Glass Shrike',
};
const SPECIES_HP: Record<string, number> = {
  ridge_stalker: 60, spore_drone: 30, duneback_brute: 140, mire_lurker: 90, crag_wyrm: 210, glass_shrike: 45,
};

export class AtlasCombatHudModule implements IModule {
  readonly name = 'AtlasCombatHud';
  private root: HTMLDivElement | null = null;
  private hpFill: HTMLDivElement | null = null;
  private hpText: HTMLSpanElement | null = null;
  private ammoWrap: HTMLDivElement | null = null;
  private ammoFill: HTMLDivElement | null = null;
  private ammoText: HTMLSpanElement | null = null;
  private ammoSig = '';
  private wasFed = true;
  private wasReloading = false;
  /** Probe/HUD diag: the feed as the HUD computes it this frame. */
  private ammoDiag: { kind: string; rounds: number; max: number; charge: number; shots: number; reloading: boolean; fed: boolean } | null = null;
  private attackBtn: HTMLButtonElement | null = null;
  private sweep: HTMLDivElement | null = null;
  private slots: HTMLDivElement | null = null;
  private targetBox: HTMLDivElement | null = null;
  /** Build-now waves 4–5: the guard pill (GUARD / PARRY), the soft-target ring, the cadence facts. */
  private guardPill: HTMLDivElement | null = null;
  private mark: HTMLDivElement | null = null;
  private markLabel: HTMLDivElement | null = null;
  private markShown = false;
  private guardShown: 'off' | 'guard' | 'parry' = 'off';
  private lastParryUs = -1n;
  private parryFlashUntil = 0;
  private lastLandedUs = 0n;
  private lastWhiffUs = 0n;
  private acc = 0;
  private slotSig = '';
  private handKey = '';
  private handObj: THREE.Object3D | null = null;

  constructor(private readonly assets: AssetModule) {}

  init(app: App): void {
    void app;
    this.buildDom();
    bus.on('atlas:account-ready', () => {
      try { atlas.conn?.subscriptionBuilder().subscribe(['SELECT * FROM player_equipment', 'SELECT * FROM player_item', 'SELECT * FROM weapon_state']); } catch { /* pre-publish */ }
    });
    // Scroll-wheel weapon ring (the input layer emits one step per notch) and
    // the view model's swing on every attack request that is not on cooldown.
    bus.on('atlas:weapon-cycle', ({ dir }) => this.cycleWeapon(dir));
    bus.on('atlas:attack', () => this.onAttack());
    // SOFT TARGET (build-now wave 5): the wilds module's ~10 Hz pick, drawn as a ring + name.
    bus.on('atlas:soft-target', (t) => {
      if (!this.mark || !this.markLabel) return;
      if (!t) { if (this.markShown) { this.markShown = false; this.mark.style.display = 'none'; } return; }
      this.mark.style.transform = `translate(${t.x.toFixed(0)}px, ${t.y.toFixed(0)}px) translate(-50%, -50%)`;
      // Ground PvP wave: a BLOCKED mark (a wall between muzzle and mark) reads red; a COMMITTED swing dims it.
      const label = t.blocked ? `${t.label} · blocked` : t.label;
      if (this.markLabel.textContent !== label) this.markLabel.textContent = label;
      const border = t.blocked ? 'color-mix(in srgb, var(--ax-bad) 95%, transparent)' : 'color-mix(in srgb, var(--ax-warn) 90%, transparent)';
      if (this.mark.style.borderColor !== border) { this.mark.style.borderColor = border; this.mark.style.boxShadow = t.blocked ? '0 0 6px color-mix(in srgb, var(--ax-bad) 60%, transparent)' : '0 0 6px color-mix(in srgb, var(--ax-warn) 55%, transparent)'; }
      const op = t.committed ? '0.55' : '1';
      if (this.mark.style.opacity !== op) this.mark.style.opacity = op;
      if (!this.markShown) { this.markShown = true; this.mark.style.display = 'block'; }
    });
    (window as unknown as Record<string, unknown>).__weaponRing = () => ({ ...this.ringDiag, inflight: this.swapInflight, queuedDir: this.queuedDir, equipped: this.equipped().spec.key || 'hands' });
    (window as unknown as Record<string, unknown>).__ammo = () => this.ammoDiag;
    app.world.addSystem(makeSystem('CombatHud', Phase.UI, 30, () => this.tick()));
  }

  // ── Weapon ring: Q / Shift+Q, or Alt+wheel (the plain wheel is the camera zoom since 2026-09-09) ───────────────────
  private swapInflight = false;
  /** A notch that arrives while a swap is in flight is QUEUED, not dropped
   *  (a fast wheel on a slow link would otherwise lose every other step). */
  private queuedDir: 1 | -1 | 0 = 0;
  private readonly ringDiag = { swaps: 0, queued: 0, lastNext: '', lastError: '', warmed: 0 };
  private warmSig = '';
  /** Decode every voice the weapon ring can make BEFORE its first trigger (the first shot of a fresh gun was silent).
   *  Re-asked only when the set of weapon KEYS in the bag changes — the store single-flights and keeps what it has. */
  private warmRingVoices(): void {
    const conn = atlas.conn;
    if (!conn?.identity) return;
    const ring: VoicedWeapon[] = [];
    for (const it of this.tbl<ItemRow>('playerItem')) {
      const spec = WEAPONS[it.itemKey];
      if (spec && it.owner.isEqual(conn.identity)) ring.push({ key: spec.key, kind: spec.kind, feed: spec.feed });
    }
    const keys = ringVoiceKeys(ring);
    const sig = keys.join('|');
    if (sig === this.warmSig) return;
    this.warmSig = sig;
    this.ringDiag.warmed = keys.length;
    sfxPreload(keys);
  }
  private cycleWeapon(dir: 1 | -1): void {
    const conn = atlas.conn;
    if (!conn?.identity) return;
    if (this.swapInflight) { this.queuedDir = dir; this.ringDiag.queued++; return; }
    const eq = this.equipped();
    const bag: RingWeapon[] = [];
    for (const it of this.tbl<ItemRow>('playerItem')) {
      if (it.owner.isEqual(conn.identity) && WEAPONS[it.itemKey]) bag.push({ itemId: it.itemId, key: it.itemKey, quality: it.quality });
    }
    const next = nextWeapon(eq.spec.key === '' ? null : { key: eq.spec.key, quality: eq.quality }, bag, dir);
    this.ringDiag.lastNext = next ? (next.key || 'hands') : 'none';
    if (!next || (next.itemId === 0n && next.key !== '')) return; // nothing to cycle to / already in hand
    const r = conn.reducers as unknown as {
      swapWeapon?: (a: { itemId: bigint }) => Promise<void>;
      unequipItem(a: { slot: string }): Promise<void>;
      useItem(a: { itemId: bigint }): Promise<void>;
    };
    this.swapInflight = true;
    sfx('sfx_equip', { gain: 0.4, cooldownMs: 120 });
    // swapWeapon is ONE transaction (never a tick with two weapons or none);
    // pre-publish bindings fall back to unequip → use.
    const p = typeof r.swapWeapon === 'function'
      ? r.swapWeapon({ itemId: next.itemId })
      : (eq.spec.key !== '' ? r.unequipItem({ slot: 'weapon' }) : Promise.resolve())
        .then(() => (next.itemId !== 0n ? r.useItem({ itemId: next.itemId }) : undefined));
    this.ringDiag.swaps++;
    void p.catch((e: Error) => { this.ringDiag.lastError = e.message; bus.emit('atlas:status', { text: `swap: ${e.message}` }); })
      .finally(() => {
        this.swapInflight = false;
        this.slotSig = '';
        const q = this.queuedDir;
        this.queuedDir = 0;
        // The rows of the swap we just awaited are applied with its ack, so the
        // queued step sees the new hand; a short defer lets a same-tick update land.
        if (q !== 0) window.setTimeout(() => this.cycleWeapon(q), 60);
      });
  }

  // ── Ammo readout (per frame, DOM written on change) ──────────────────────
  private updateAmmo(): void {
    const conn = atlas.conn;
    const wrap = this.ammoWrap;
    if (!conn?.identity || !wrap || !this.ammoFill || !this.ammoText) return;
    const { spec } = this.equippedCache ?? (this.equippedCache = this.equipped());
    const feed = spec.feed;
    if (!feed) { if (wrap.style.display !== 'none') wrap.style.display = 'none'; this.ammoDiag = null; return; }
    // weapon_state is UNIQUE on identity: the index answers in O(1) (fast_find) — this ran every frame over the whole table.
    let row: WeaponStateRow | null = null;
    try { row = ((conn.db as unknown as { weaponState?: { identity?: { find(i: unknown): WeaponStateRow | null | undefined } } }).weaponState?.identity?.find(conn.identity)) ?? null; }
    catch { for (const w of this.tbl<WeaponStateRow>('weaponState')) if (w.identity.isEqual(conn.identity)) { row = w; break; } }
    const fresh = !row || row.itemKey !== spec.key; // no row yet for this weapon: a full feed
    const nowMs = Date.now();
    let text = '', frac = 1, fed = true, reloading = false, rounds = 0, max = 0, charge = 0, shots = 0;
    if (feed.kind === 'magazine') {
      max = feed.rounds;
      const reloadUntil = fresh ? 0 : Number(row!.reloadUntil.microsSinceUnixEpoch / 1000n);
      const leftMs = reloadUntil > 0 ? reloadUntil - nowMs : 0;
      reloading = leftMs > 0;
      rounds = fresh ? max : (reloadUntil > 0 && leftMs <= 0 ? max : row!.rounds);
      fed = feedReady(feed, { rounds, charge: 0, reloadLeftMs: Math.max(0, leftMs) }).ok;
      frac = reloading ? 1 - leftMs / feed.reloadMs : rounds / max;
      text = reloading ? `RELOADING ${(leftMs / 1000).toFixed(1)} s` : `${rounds} / ${max}`;
    } else {
      charge = fresh ? feed.chargeMax : chargeAfter(row!.charge, feed, nowMs - Number(row!.chargeAt.microsSinceUnixEpoch / 1000n));
      shots = Math.floor(charge / feed.shotCost);
      max = shotsPerCharge(feed);
      fed = charge >= feed.shotCost;
      frac = charge / feed.chargeMax;
      text = fed ? `CHARGE ${Math.round(charge)}%  ·  ${shots} shot${shots === 1 ? '' : 's'}` : `RECHARGING ${Math.round(charge)}%`;
    }
    // Cues on the edges: a reload starting, a capacitor becoming ready again.
    if (reloading && !this.wasReloading) sfx('sfx_reload_magazine', { gain: 0.5, cooldownMs: 300 });
    if (fed && !this.wasFed && feed.kind === 'energy') sfx('sfx_recharge_capacitor', { gain: 0.35, cooldownMs: 400 });
    this.wasReloading = reloading; this.wasFed = fed;
    this.ammoDiag = { kind: feed.kind, rounds, max, charge: Math.round(charge * 10) / 10, shots, reloading, fed };
    const sig = `${text}|${Math.round(frac * 100)}|${fed}`;
    if (sig === this.ammoSig) return;
    this.ammoSig = sig;
    if (wrap.style.display !== 'block') wrap.style.display = 'block';
    if (this.ammoText.textContent !== text) this.ammoText.textContent = text; // no layout work when unchanged
    this.ammoFill.style.width = `${Math.max(0, Math.min(1, frac)) * 100}%`;
    this.ammoFill.style.background = fed
      ? (feed.kind === 'energy' ? 'linear-gradient(90deg,color-mix(in srgb, var(--ax-info) 62%, var(--ax-bg)),var(--ax-info))' : 'linear-gradient(90deg,color-mix(in srgb, var(--ax-accent) 62%, var(--ax-bg)),var(--ax-accent))')
      : 'linear-gradient(90deg,color-mix(in srgb, var(--ax-bad) 62%, var(--ax-bg)),var(--ax-bad))';
  }

  // ── The view model's swing ───────────────────────────────────────────────
  private swingAt = -Infinity;
  private swingKind: 'gun' | 'melee' = 'melee';
  private onAttack(): void {
    const { spec } = this.equippedCache ?? (this.equippedCache = this.equipped());
    const sinceMs = this.lastStrikeUs === 0n ? 1e9 : Number(BigInt(Date.now()) * 1000n - this.lastStrikeUs) / 1000;
    if (sinceMs < this.cadenceMs(spec) * 0.9) return; // the server will refuse it: no swing for a refused swing
    this.swingAt = performance.now();
    this.swingKind = spec.kind;
  }

  /** The cadence the server will hold the next swing to: ×1.5 after a whiff,
   *  ×0.7 after a landed hit (build-now wave 5) — the same mirrored rule. */
  private cadenceMs(spec: WeaponSpec): number {
    const whiffed = this.lastWhiffUs !== 0n && this.lastWhiffUs >= this.lastStrikeUs;
    const landed = this.lastLandedUs !== 0n && this.lastLandedUs >= this.lastStrikeUs;
    return requiredCooldownMs(spec.cooldownMs, whiffed, landed);
  }

  private tbl<T>(name: string): Iterable<T> {
    const t = (atlas.conn?.db as unknown as Record<string, { iter(): Iterable<T> } | undefined>)[name];
    try { return t ? t.iter() : []; } catch { return []; }
  }

  /** MY rows of a table through its owner btree (lib/fast_find turns it into a Map lookup, in table order) —
   *  the HUD walked the shard-wide player_item / player_equipment tables (1 890 item rows on the live shard)
   *  several times a second to find its own few (2026-09-22). Falls back to the old filtered walk. */
  private mine<T extends { owner: { isEqual(o: unknown): boolean } }>(table: string, index: string): Iterable<T> {
    const conn = atlas.conn;
    if (!conn?.identity) return [];
    const t = (conn.db as unknown as Record<string, Record<string, { filter?(v: unknown): Iterable<T> }> | undefined>)[table];
    try {
      const idx = t?.[index];
      if (idx && typeof idx.filter === 'function') return idx.filter(conn.identity);
    } catch { /* fall back to the walk */ }
    const me = conn.identity;
    return [...this.tbl<T>(table)].filter((r) => r.owner.isEqual(me));
  }

  private equipped(): { spec: WeaponSpec; quality: number } {
    const conn = atlas.conn;
    if (conn?.identity) {
      for (const eq of this.mine<EquipRow>('playerEquipment', 'playerEquipmentByOwner')) {
        if (eq.slot === 'weapon' && eq.owner.isEqual(conn.identity) && WEAPONS[eq.itemKey]) {
          return { spec: WEAPONS[eq.itemKey], quality: eq.quality };
        }
      }
    }
    return { spec: FISTS, quality: 0 };
  }

  // ── DOM ────────────────────────────────────────────────────────────────────
  private buildDom(): void {
    const root = document.createElement('div');
    root.id = 'combat-hud';
    root.style.cssText = 'position:fixed;left:50%;bottom:80px;transform:translateX(-50%);z-index:39;display:none;'  // HUD ladder v2: directly above the menu bar (10 + 63 + gap)
      + 'flex-direction:column;align-items:center;gap:5px;font:12px var(--ax-font);color:var(--ax-text);pointer-events:none';
    // Target frame
    const tf = document.createElement('div');
    tf.id = 'combat-target';
    tf.style.cssText = 'display:none;min-width:230px;padding:4px 10px;border-radius:var(--ax-radius);background:color-mix(in srgb, var(--ax-bg) 78%, transparent);'
      + 'border:1px solid var(--ax-line2);text-align:center';
    this.targetBox = tf;
    root.appendChild(tf);
    // Player health
    const hpWrap = document.createElement('div');
    hpWrap.style.cssText = 'width:300px;height:16px;border-radius:var(--ax-radius);background:color-mix(in srgb, var(--ax-bg) 85%, transparent);'
      + 'border:1px solid var(--ax-line);overflow:hidden;position:relative';
    this.hpFill = document.createElement('div');
    this.hpFill.style.cssText = 'height:100%;width:100%;background:linear-gradient(90deg,var(--ax-sel),var(--ax-good));transition:width .25s';
    hpWrap.appendChild(this.hpFill);
    this.hpText = document.createElement('span');
    this.hpText.style.cssText = 'position:absolute;inset:0;text-align:center;font:700 11px var(--ax-font);line-height:16px;'
      + 'color:var(--ax-text);text-shadow:0 1px 2px #000';
    hpWrap.appendChild(this.hpText);
    root.appendChild(hpWrap);
    // Guard pill (build-now wave 4): shows while the right button holds the
    // guard; flashes PARRY when the row records one.
    const guard = document.createElement('div');
    guard.id = 'combat-guard';
    guard.style.cssText = 'display:none;padding:2px 12px;border-radius:var(--ax-win-radius);background:color-mix(in srgb, var(--ax-bg3) 88%, transparent);border:1px solid var(--ax-line2);'
      + 'font:700 10px var(--ax-font);letter-spacing:0.1em;color:var(--ax-text);text-shadow:0 1px 2px #000';
    guard.textContent = 'GUARD';
    root.appendChild(guard);
    this.guardPill = guard;
    // Soft-target ring (build-now wave 5): fixed to the screen point of the mark.
    const mark = document.createElement('div');
    mark.id = 'combat-mark';
    mark.style.cssText = 'position:fixed;left:0;top:0;z-index:38;display:none;pointer-events:none;width:26px;height:26px;border-radius:50%;'
      + 'border:2px solid color-mix(in srgb, var(--ax-accent) 90%, transparent);box-shadow:0 0 6px color-mix(in srgb, var(--ax-warn) 55%, transparent);transform:translate(-50%,-50%)';
    const markLabel = document.createElement('div');
    markLabel.style.cssText = 'position:absolute;top:28px;left:50%;transform:translateX(-50%);white-space:nowrap;font:600 11px var(--ax-font);color:var(--ax-text);text-shadow:0 1px 2px #000';
    mark.appendChild(markLabel);
    document.body.appendChild(mark);
    this.mark = mark;
    this.markLabel = markLabel;
    // Ammo (2026-09-05): a magazine's rounds + reload sweep, or a capacitor's charge.
    const ammoWrap = document.createElement('div');
    ammoWrap.id = 'combat-ammo';
    ammoWrap.style.cssText = 'display:none;width:300px;height:14px;border-radius:var(--ax-radius);background:color-mix(in srgb, var(--ax-bg) 85%, transparent);'
      + 'border:1px solid var(--ax-line);overflow:hidden;position:relative';
    this.ammoFill = document.createElement('div');
    this.ammoFill.style.cssText = 'height:100%;width:100%;background:linear-gradient(90deg,color-mix(in srgb, var(--ax-accent) 48%, var(--ax-bg2)),var(--ax-accent))';
    ammoWrap.appendChild(this.ammoFill);
    this.ammoText = document.createElement('span');
    this.ammoText.style.cssText = 'position:absolute;inset:0;text-align:center;font:700 10px var(--ax-font);line-height:14px;'
      + 'color:var(--ax-text);text-shadow:0 1px 2px #000;letter-spacing:0.04em';
    ammoWrap.appendChild(this.ammoText);
    root.appendChild(ammoWrap);
    this.ammoWrap = ammoWrap;
    // Action bar
    const bar = document.createElement('div');
    bar.id = 'combat-actionbar';
    bar.style.cssText = 'display:flex;gap:6px;align-items:flex-end;pointer-events:auto';
    const atk = document.createElement('button');
    atk.id = 'combat-attack';
    atk.title = 'Attack the nearest creature in reach (F, or left click with the cursor locked)';
    atk.style.cssText = 'position:relative;width:52px;height:52px;border-radius:var(--ax-win-radius);background:var(--ax-bg3);'
      + 'border:1px solid var(--ax-line2);color:var(--ax-text);font:700 20px var(--ax-font);cursor:pointer;overflow:hidden';
    atk.textContent = '⚔';
    const sweep = document.createElement('div');
    sweep.style.cssText = 'position:absolute;left:0;bottom:0;width:100%;height:0%;background:color-mix(in srgb, var(--ax-bg2) 75%, transparent);pointer-events:none';
    atk.appendChild(sweep);
    this.sweep = sweep;
    atk.onclick = () => bus.emit('atlas:attack', { source: 'touch' });
    this.attackBtn = atk;
    bar.appendChild(atk);
    const slots = document.createElement('div');
    slots.id = 'combat-weapons';
    slots.style.cssText = 'display:flex;gap:5px;align-items:flex-end';
    this.slots = slots;
    bar.appendChild(slots);
    root.appendChild(bar);
    document.body.appendChild(root);
    this.root = root;
  }

  /** UI tick (Phase.UI/30): visibility, vitals, sweep, target, hand model. */
  private tick(): void {
    const conn = atlas.conn;
    const onGround = conn !== null && atlas.currentWorldId !== null
      && atlas.currentWorldId === atlas.commonsWorldId && !atlas.spaceFlight;
    if (this.root && onGround !== this.rootShown) { this.rootShown = onGround; this.root.style.display = onGround ? 'flex' : 'none'; } // write on change
    this.animateHand(onGround);
    if (onGround && conn) this.updateAmmo();
    if (!onGround || !conn) return;
    if (++this.acc % 8 !== 0) { this.updateSweep(); return; } // heavy scans at ~7 Hz
    this.equippedCache = this.equipped(); // the sweep reads the cached weapon spec between scans
    this.warmRingVoices();
    const meHex = atlas.identityHex;
    // Vitals
    let v: VitalsRow | null = null;
    for (const row of this.tbl<VitalsRow>('playerVitals')) {
      if (meHex !== null && row.identity.toHexString() === meHex) { v = row; break; }
    }
    const hp = v?.hp ?? 100, maxHp = v?.maxHp ?? 100;
    if (this.hpFill) {
      const f = Math.max(0, Math.min(1, hp / maxHp));
      this.hpFill.style.width = `${f * 100}%`;
      this.hpFill.style.background = f > 0.35
        ? 'linear-gradient(90deg,color-mix(in srgb, var(--ax-good) 62%, var(--ax-bg)),var(--ax-good))' : 'linear-gradient(90deg,color-mix(in srgb, var(--ax-bad) 62%, var(--ax-bg)),var(--ax-bad))';
    }
    if (this.hpText) { const t = `${hp} / ${maxHp}`; if (this.hpText.textContent !== t) this.hpText.textContent = t; }
    this.lastStrikeUs = v ? v.lastStrikeAt.microsSinceUnixEpoch : 0n;
    // Build-now waves 4–5: my stance row (guard, parry, hit-confirm) and my whiff stamp.
    let stance: StanceRow | null = null;
    for (const s of this.tbl<StanceRow>('playerStance')) {
      if (meHex !== null && s.identity.toHexString() === meHex) { stance = s; break; }
    }
    if (stance) {
      const parryUs = stance.lastParryAt.microsSinceUnixEpoch;
      if (this.lastParryUs === -1n) this.lastParryUs = parryUs; // first sight: no flash for history
      else if (parryUs !== this.lastParryUs) {
        // A PARRY landed on the row: the heavy hit-stop, a nod, a clang, the pill flashes.
        this.lastParryUs = parryUs;
        hitStop(performance.now(), true);
        cameraImpulse(-KICK_DEALT_PITCH, 0);
        sfx('sfx_impact_energy', { gain: 0.6, cooldownMs: 200 });
        this.parryFlashUntil = performance.now() + 650;
      }
      this.lastLandedUs = stance.lastLandedAt.microsSinceUnixEpoch;
    }
    for (const a of this.tbl<ActionRow>('playerAction')) {
      if (meHex !== null && a.identity.toHexString() === meHex) { this.lastWhiffUs = a.lastWhiffAt.microsSinceUnixEpoch; break; }
    }
    const guarding = atlas.guardHeld || (!!stance && stance.guardRaisedAt.microsSinceUnixEpoch > stance.guardReleasedAt.microsSinceUnixEpoch);
    const want: 'off' | 'guard' | 'parry' = performance.now() < this.parryFlashUntil ? 'parry' : guarding ? 'guard' : 'off';
    if (this.guardPill && want !== this.guardShown) {
      this.guardShown = want;
      this.guardPill.style.display = want === 'off' ? 'none' : 'block';
      if (want !== 'off') {
        this.guardPill.textContent = want === 'parry' ? 'PARRY' : 'GUARD';
        this.guardPill.style.color = want === 'parry' ? 'var(--ax-text)' : 'var(--ax-text)';
        this.guardPill.style.borderColor = want === 'parry' ? 'var(--ax-accent)' : 'var(--ax-line2)';
      }
    }
    this.updateSweep();
    this.updateTarget();
    this.updateSlots();
    void this.updateHand();
  }

  private lastStrikeUs = 0n;
  private rootShown: boolean | null = null;
  private equippedCache: { spec: WeaponSpec; quality: number } | null = null;
  private sweepHeight = '';

  private updateSweep(): void {
    if (!this.sweep) return;
    // The weapon spec comes from the ~7 Hz scan in tick() — the per-frame
    // sweep used to re-scan player_equipment on the gate's OTHER 7 frames too.
    const { spec } = this.equippedCache ?? (this.equippedCache = this.equipped());
    const sinceMs = this.lastStrikeUs === 0n ? 1e9 : Number(BigInt(Date.now()) * 1000n - this.lastStrikeUs) / 1000;
    const frac = Math.max(0, 1 - sinceMs / this.cadenceMs(spec));
    const h = `${frac * 100}%`;
    if (h !== this.sweepHeight) { this.sweepHeight = h; this.sweep.style.height = h; } // idle sweep: no per-frame style write
  }

  private updateTarget(): void {
    const box = this.targetBox;
    if (!box) return;
    let best: CreatureRow | null = null;
    let bestD = 55;
    for (const c of this.tbl<CreatureRow>('creature')) {
      if (c.state === 'dead') continue;
      const d = Math.hypot(c.x - atlas.localX, c.z - atlas.localZ);
      if (d < bestD) { best = c; bestD = d; }
    }
    if (!best) { box.style.display = 'none'; return; }
    const { spec } = this.equipped();
    const maxHp = SPECIES_HP[best.species] ?? 60;
    const frac = Math.max(0, Math.min(1, best.hp / maxHp));
    box.style.display = 'block';
    box.textContent = '';
    const name = document.createElement('div');
    name.style.cssText = 'font-weight:700;color:var(--ax-text)';
    { const t = `${PLATE[best.species] ?? best.species}  ·  ${Math.round(bestD)} m${bestD <= spec.rangeM ? ' — in reach' : ''}`; if (name.textContent !== t) name.textContent = t; }
    const barWrap = document.createElement('div');
    barWrap.style.cssText = 'height:8px;border-radius:var(--ax-radius);background:var(--ax-bg2);border:1px solid var(--ax-line2);overflow:hidden;margin-top:3px';
    const fill = document.createElement('div');
    fill.style.cssText = `height:100%;width:${frac * 100}%;background:${frac > 0.4 ? 'var(--ax-good)' : 'var(--ax-bad)'}`;
    barWrap.appendChild(fill);
    box.appendChild(name);
    box.appendChild(barWrap);
  }

  private updateSlots(): void {
    const conn = atlas.conn;
    const slots = this.slots;
    if (!conn?.identity || !slots) return;
    const eqNow = this.equipped();
    const eqSpec = eqNow.spec;
    const owned: Array<{ itemId: bigint; key: string; quality: number }> = [];
    for (const it of this.mine<ItemRow>('playerItem', 'playerItemByOwner')) {
      if (it.owner.isEqual(conn.identity) && WEAPONS[it.itemKey]) owned.push({ itemId: it.itemId, key: it.itemKey, quality: it.quality });
    }
    owned.sort((a, b) => WEAPONS[a.key].tier - WEAPONS[b.key].tier);
    const sig = `${eqSpec.key}|${owned.map((o) => `${o.itemId}:${o.key}`).join(',')}`;
    if (sig === this.slotSig) return;
    this.slotSig = sig;
    slots.textContent = '';
    const mk = (key: string, quality: number, equippedNow: boolean, onClick: () => void): HTMLButtonElement => {
      const w = WEAPONS[key];
      const b = document.createElement('button');
      b.style.cssText = `position:relative;width:44px;height:44px;border-radius:var(--ax-radius);cursor:pointer;overflow:hidden;`
        + `background:var(--ax-bg2);border:1px solid ${equippedNow ? 'var(--ax-accent)' : 'var(--ax-line)'};padding:0`;
      b.title = `${w.name} (T${w.tier}${quality ? ` · q${quality}` : ''})\n${weaponDamage(w, quality)} dmg · ${w.cooldownMs} ms · ${w.rangeM} m\n${w.desc}\n${equippedNow ? 'Click to unequip' : 'Click to equip'}`;
      const img = document.createElement('img');
      img.src = `${ASSET_STORE_BASE}/textures/icons/${key}.png`;
      img.style.cssText = 'width:100%;height:100%;object-fit:cover';
      img.onerror = () => { img.remove(); b.textContent = w.kind === 'gun' ? '🔫' : '🗡'; };
      b.appendChild(img);
      const pips = document.createElement('div');
      pips.style.cssText = 'position:absolute;left:3px;bottom:2px;font:700 8px var(--ax-font);color:var(--ax-hi);text-shadow:0 1px 2px #000';
      pips.textContent = '●'.repeat(w.tier);
      b.appendChild(pips);
      b.onclick = onClick;
      return b;
    };
    if (eqSpec.key !== '') {
      slots.appendChild(mk(eqSpec.key, eqNow.quality, true, () => {
        sfx('sfx_equip', { gain: 0.4, cooldownMs: 250 });
        void (conn.reducers as unknown as { unequipItem(a: { slot: string }): Promise<void> })
          .unequipItem({ slot: 'weapon' }).catch((e: Error) => bus.emit('atlas:status', { text: `unequip: ${e.message}` }));
        this.slotSig = '';
      }));
    }
    for (const o of owned) {
      slots.appendChild(mk(o.key, o.quality, false, () => {
        sfx('sfx_equip', { gain: 0.4, cooldownMs: 250 });
        void (conn.reducers as unknown as { useItem(a: { itemId: bigint }): Promise<void> })
          .useItem({ itemId: o.itemId }).catch((e: Error) => bus.emit('atlas:status', { text: `equip: ${e.message}` }));
        this.slotSig = '';
      }));
    }
  }

  /** The equipped weapon as a FIRST-PERSON view model: parented to the
   *  camera (lower right, ahead of the 0.05 m near plane) so the player SEES
   *  it — the old hip anchor (+0.18 z on the body) sat behind the eye and
   *  never showed. Recentred on its bounding box, muzzle/edge forward. Hidden
   *  in fly/vehicle/space/XR (the headset has hands of its own). */
  private handBase = { x: 0, y: 0, z: 0, rx: 0, ry: 0, rz: 0 };
  private lastHandMs = 0;
  private swayX = 0;
  private swayYaw = 0;
  private lastYaw = 0;
  private bobPhase = 0;
  private reloadDip = 0;
  private async updateHand(): Promise<void> {
    const { spec } = this.equipped();
    if (spec.key === this.handKey) return;
    const rig = atlas.localMesh;
    if (this.handObj) { this.handObj.parent?.remove(this.handObj); this.handObj = null; }
    this.handKey = spec.key;
    if (spec.key === '' || !rig) return;
    const cam = rig.children.find((c) => (c as { isCamera?: boolean }).isCamera) ?? null;
    if (!cam) return;
    const conn = atlas.conn;
    if (!conn) return;
    const id = prefabAssetId(conn, `prop:weapon_${spec.key}`);
    if (id === null) return; // model not generated yet — the icon still shows
    try {
      const master = await this.assets.load(id);
      if (this.handKey !== spec.key) return; // swapped while loading
      const obj = master.clone();
      const gun = spec.kind === 'gun';
      const box = new THREE.Box3().setFromObject(obj);
      const len = Math.max(0.01, box.max.x - box.min.x, box.max.y - box.min.y, box.max.z - box.min.z);
      obj.scale.setScalar((gun ? 0.62 : 0.72) / len);
      // Muzzle / edge FORWARD (−Z in camera space). Guns seat at −π/2: the
      // hip anchor's +π/2 showed every barrel pointing at the player once the
      // model moved in front of the eye (field report 2026-09-05: "the guns
      // are backwards") — a 180° turn from that seat.
      obj.rotation.set(0, gun ? -Math.PI / 2 : -Math.PI / 2, gun ? 0 : -Math.PI / 5);
      obj.updateMatrixWorld(true);
      const pivot = new THREE.Group();
      pivot.add(obj);
      const b2 = new THREE.Box3().setFromObject(obj);
      obj.position.sub(b2.getCenter(new THREE.Vector3())); // the pivot IS the model's centre
      obj.traverse((o) => { o.frustumCulled = false; (o as THREE.Mesh).renderOrder = 20; });
      // Lower-right, turned a little INTO the crosshair (a +Y turn points −Z toward −X).
      this.handBase = gun
        ? { x: 0.30, y: -0.24, z: -0.60, rx: 0.04, ry: 0.14, rz: 0 }
        : { x: 0.34, y: -0.28, z: -0.54, rx: 0.10, ry: 0.28, rz: -0.18 };
      pivot.position.set(this.handBase.x, this.handBase.y, this.handBase.z);
      pivot.rotation.set(this.handBase.rx, this.handBase.ry, this.handBase.rz);
      pivot.name = 'weapon-view-model';
      cam.add(pivot);
      this.handObj = pivot;
      this.lastYaw = atlas.localYaw;
    } catch { /* asset load failed — icon-only */ }
  }

  /** Per frame: visibility, look sway (the weapon lags a turn), a walk bob,
   *  and the strike swing — a melee arc forward-and-down or a gun's kick back
   *  with a spring return, over ~220 ms. Dosed: the view model is a cue, not
   *  a show. */
  private animateHand(onGround: boolean): void {
    const h = this.handObj;
    if (!h) return;
    // The placement map: the gun in hand leaves the picture (the avatar shows the body instead). Third person too
    // (2026-10-07): this gate runs every frame, after the avatar module hid the view model, and put the camera-fixed gun
    // back in the corner of a third-person picture (foot-camera-probe: "the view model is hidden (true)").
    const visible = onGround && !atlas.flyMode && !atlas.hoverRide && !atlas.skyFlight && !atlas.xrPresenting && !atlas.editMode && !atlas.placementView && !atlas.thirdPerson;
    if (h.visible !== visible) h.visible = visible;
    if (!visible) return;
    // (slice H, 2026-10-02) THE GUN IN HAND IS PART OF THE BODY: indoors it wore its lit PBR material and drew BLACK in
    // every room (the world legs' pictures) — it wears the room's light like the bodies do, its own material outside.
    presentIndoors(h, atlas.interiorId !== null);
    const now = performance.now();
    // C6: a per-frame fixed fraction ran ~2.4x faster at 144 Hz than at 60 — every ease below is in real time.
    const rawDt = (now - this.lastHandMs) / 1000;
    const dtH = Math.min(0.1, Math.max(0.001, rawDt));
    this.lastHandMs = now;
    // Look sway: lag the yaw change, spring back. The sway and the walk bob were still per FRAME (the sweep that fixed the
    // reload dip left them): at 144 Hz the bob ran 2.4x as fast and the sway was 2.4x smaller. The yaw change is taken as
    // a rate (per 1/60 s), the eases as 1 - e^(-k dt) with k = -ln(1 - f) * 60 for the old per-frame fractions f.
    let dYaw = atlas.localYaw - this.lastYaw;
    if (dYaw > Math.PI) dYaw -= Math.PI * 2; else if (dYaw < -Math.PI) dYaw += Math.PI * 2;
    this.lastYaw = atlas.localYaw;
    // The RATE takes the TRUE interval: over the ease's 0.1 s clamp a hitch read as a flick (the gun snapped to both
    // clamps), and below 10 fps the sway was always too large. A gap past a quarter second — a stall, or the gun was
    // hidden (that path stamps nothing) — is no reading at all.
    const yawStep = rawDt > 0.25 ? 0 : dYaw / (Math.max(rawDt, 1 / 240) * 60);
    this.swayX += (Math.max(-0.05, Math.min(0.05, -yawStep * 0.6)) - this.swayX) * (1 - Math.exp(-25.85 * dtH));
    this.swayYaw += (Math.max(-0.12, Math.min(0.12, yawStep * 1.2)) - this.swayYaw) * (1 - Math.exp(-21.4 * dtH));
    // Walk bob: only while there is movement intent; faster + wider at a sprint.
    const moving = atlas.intentX !== 0 || atlas.intentZ !== 0;
    if (moving) this.bobPhase += (atlas.intentMode === 1 ? 11.4 : 7.8) * dtH;
    const bobY = moving ? Math.abs(Math.sin(this.bobPhase)) * (atlas.intentMode === 1 ? 0.018 : 0.011) : 0;
    const bobX = moving ? Math.sin(this.bobPhase * 0.5) * 0.008 : 0;
    // Strike swing.
    const u = (now - this.swingAt) / 220;
    let sx = 0, sy = 0, sz = 0, srx = 0, sry = 0;
    if (u >= 0 && u < 1) {
      const a = Math.sin(u * Math.PI);
      if (this.swingKind === 'melee') { srx = -0.9 * a; sry = -0.35 * a; sz = -0.16 * a; sx = -0.10 * a; sy = -0.04 * a; }
      else { sz = 0.07 * (1 - u) * (1 - u); srx = 0.16 * (1 - u) * (1 - u); sy = 0.015 * a; }
    }
    // Reloading: the gun dips out of the eyeline and tilts, then comes back.
    const dip = this.ammoDiag?.reloading ? 1 : 0;
    // The same ease in real time (k = -ln(0.85)*60 ≈ 9.75 /s).
    this.reloadDip += (dip - this.reloadDip) * (1 - Math.exp(-9.75 * dtH));
    const b = this.handBase;
    h.position.set(b.x + this.swayX + bobX + sx + 0.04 * this.reloadDip, b.y - bobY + sy - 0.16 * this.reloadDip, b.z + sz + 0.05 * this.reloadDip);
    h.rotation.set(b.rx + srx + 0.55 * this.reloadDip, b.ry + this.swayYaw + sry, b.rz - 0.35 * this.reloadDip);
  }

  dispose(): void {
    this.root?.remove();
    if (this.handObj) this.handObj.parent?.remove(this.handObj);
  }
}
